// =============================================================================
// LibraryBulkBar — shared bulk-action bar (Assets page + Batch review)
// =============================================================================
// Floating bottom bar shown while a selection exists. Upgraded metadata ops:
//   - category assign/clear (searchable Combobox, confirm before apply)
//   - multi-tag add with inline tag creation (staged, applied with a button)
//   - bulk keyword append (comma-separated input → bulkUpdate.addKeywords)
//   - gallery show/hide (bulkUpdate.galleryVisible)
//   - archive (assets page only) + conditional "Delete failed"
// The bar owns its mutations/confirm dialog; the parent only supplies the
// selection and clears it via onDone (also invoked after every success).
// =============================================================================

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Combobox, Input, useConfirm } from '@tkpod/ui';
import type { ComboboxOption } from '@tkpod/ui';
import type { LibraryCategoryNode, LibraryTagSummary } from '@tkpod/shared';
import { toast } from '@/lib/toast';
import { bulkDeleteFailed, bulkUpdate, createTag, flattenCategories, libraryKeys } from './api';

/** Sentinel for the "clear category" option (bulkUpdate categoryId: null). */
const CLEAR_CATEGORY = '__clear__';

export interface LibraryBulkBarProps {
  selectedIds: string[];
  categories: LibraryCategoryNode[];
  tags: LibraryTagSummary[];
  /** Selection contains at least one FAILED asset → show "Delete failed". */
  hasFailedSelected: boolean;
  /** Show the Archive action (Assets page; review uses per-row discard). */
  showArchive?: boolean;
  /** Clear the parent's selection (called after every successful action). */
  onDone: () => void;
}

export function LibraryBulkBar({
  selectedIds,
  categories,
  tags,
  hasFailedSelected,
  showArchive = false,
  onDone,
}: LibraryBulkBarProps) {
  const queryClient = useQueryClient();
  const { confirm, dialog } = useConfirm();

  const [stagedTagIds, setStagedTagIds] = useState<string[]>([]);
  const [keywordsInput, setKeywordsInput] = useState('');

  const categoryOptions: ComboboxOption[] = [
    { value: CLEAR_CATEGORY, label: 'No category (clear)' },
    ...flattenCategories(categories),
  ];
  const tagOptions: ComboboxOption[] = tags.map((t) => ({ value: t.id, label: t.name }));

  const bulkMutation = useMutation({
    mutationFn: bulkUpdate,
    onSuccess: (res) => {
      toast(`Updated ${res.updated} asset(s)`, 'success');
      if (res.skipped?.length) {
        toast(
          `${res.skipped.length} asset(s) skipped — e.g. "${res.skipped[0].sku}": ${res.skipped[0].reason}`,
          'warning',
        );
      }
      setStagedTagIds([]);
      setKeywordsInput('');
      void queryClient.invalidateQueries({ queryKey: ['library'] });
      onDone();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const deleteFailedMutation = useMutation({
    mutationFn: () => bulkDeleteFailed(selectedIds),
    onSuccess: (res) => {
      toast(
        res.deleted ? `Deleted ${res.deleted} failed asset(s)` : 'No FAILED assets in selection',
        res.deleted ? 'success' : 'info',
      );
      void queryClient.invalidateQueries({ queryKey: ['library'] });
      onDone();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const run = async (body: Record<string, unknown>, label: string, danger = false) => {
    const ok = await confirm({
      title: label,
      description: `Apply to ${selectedIds.length} selected asset(s)?`,
      confirmLabel: 'Apply',
      tone: danger ? 'danger' : 'default',
    });
    if (ok) bulkMutation.mutate({ assetIds: selectedIds, ...body });
  };

  const onCategoryPick = (value: string | null) => {
    if (!value) return;
    void run(
      { categoryId: value === CLEAR_CATEGORY ? null : value },
      value === CLEAR_CATEGORY ? 'Clear category' : 'Assign category',
    );
  };

  const onCreateTag = async (name: string): Promise<ComboboxOption> => {
    try {
      const created = await createTag({ name });
      void queryClient.invalidateQueries({ queryKey: libraryKeys.tags });
      return { value: created.id, label: created.name };
    } catch (e) {
      toast((e as Error).message, 'error');
      throw e;
    }
  };

  const applyTags = () => {
    if (!stagedTagIds.length) return;
    void run({ addTagIds: stagedTagIds }, `Add ${stagedTagIds.length} tag(s)`);
  };

  const applyKeywords = () => {
    const parsed = keywordsInput.split(',').map((k) => k.trim()).filter(Boolean);
    if (!parsed.length) return;
    void run({ addKeywords: parsed }, `Add ${parsed.length} keyword(s)`);
  };

  const onDeleteFailed = async () => {
    const ok = await confirm({
      title: 'Delete failed assets',
      description: `Permanently delete the FAILED assets among the ${selectedIds.length} selected? Other assets are left untouched.`,
      confirmLabel: 'Delete failed',
      tone: 'danger',
    });
    if (ok) deleteFailedMutation.mutate();
  };

  if (!selectedIds.length) return null;

  return (
    <>
      {dialog}
      <div className="fixed bottom-6 left-1/2 z-30 flex w-[min(64rem,calc(100vw-2rem))] -translate-x-1/2 flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-brand-800 bg-brand-950 px-4 py-3 shadow-lg">
        <span className="shrink-0 text-sm font-medium text-white">
          {selectedIds.length} selected
        </span>

        {/* Category — pick applies immediately (after confirm) */}
        <div className="w-48">
          <Combobox
            size="sm"
            options={categoryOptions}
            value={null}
            onChange={onCategoryPick}
            placeholder="Assign category…"
            searchPlaceholder="Search categories…"
            allowClear={false}
          />
        </div>

        {/* Tags — staged multi-select (with inline create) + explicit apply */}
        <div className="flex min-w-56 flex-1 items-center gap-1.5">
          <Combobox
            multiple
            size="sm"
            options={tagOptions}
            value={stagedTagIds}
            onChange={setStagedTagIds}
            placeholder="Add tags…"
            searchPlaceholder="Search or create tags…"
            onCreate={onCreateTag}
          />
          <Button
            size="sm"
            variant="secondary"
            disabled={!stagedTagIds.length}
            loading={bulkMutation.isPending}
            onClick={applyTags}
          >
            Apply
          </Button>
        </div>

        {/* Keywords — comma-separated append */}
        <div className="flex min-w-52 items-center gap-1.5">
          <Input
            placeholder="keyword, keyword…"
            value={keywordsInput}
            onChange={(e) => setKeywordsInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyKeywords()}
            className="!py-1 text-xs"
          />
          <Button
            size="sm"
            variant="secondary"
            disabled={!keywordsInput.trim()}
            loading={bulkMutation.isPending}
            onClick={applyKeywords}
          >
            Add
          </Button>
        </div>

        {/* Gallery visibility — hiding a primary also hides its variants
            (the gallery lists primaries only). Ghost buttons default to gray
            text meant for light surfaces; the bar is dark, so force white. */}
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            className="!text-white/85 hover:!bg-white/10 hover:!text-white"
            onClick={() => void run({ galleryVisible: true }, 'Show in gallery')}
          >
            Show in gallery
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="!text-white/85 hover:!bg-white/10 hover:!text-white"
            onClick={() => void run({ galleryVisible: false }, 'Hide from gallery')}
          >
            Hide
          </Button>
        </div>

        {showArchive && (
          <Button
            variant="danger"
            size="sm"
            onClick={() => void run({ archive: true }, 'Archive assets', true)}
          >
            Archive
          </Button>
        )}
        {/* Only offered when the selection actually contains FAILED assets —
            an always-visible "Delete failed" reads like it deletes everything. */}
        {hasFailedSelected && (
          <Button
            variant="ghost"
            size="sm"
            className="!text-red-400 hover:!bg-white/10"
            loading={deleteFailedMutation.isPending}
            onClick={() => void onDeleteFailed()}
          >
            Delete failed
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="!text-white/85 hover:!bg-white/10 hover:!text-white"
          onClick={onDone}
        >
          Clear
        </Button>
      </div>
    </>
  );
}
